import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    status: string;
    description: string;
    active: boolean;
    blocked: boolean;
    disabled: boolean;
    canRestore: boolean;
    changeCount: number;
    selectedChange: number;
    onNavigate: (direction: number) => void;
    onKeep: () => void;
    onRestore: () => void;
}

const SummaryChangesReview: React.FC<Props> = ({ status, description, active, blocked, disabled, canRestore, onKeep, onRestore, changeCount, selectedChange, onNavigate }) => {
    const { t } = useTranslation();
    const details = useRef<HTMLDetailsElement>(null);
    const helpTrigger = useRef<HTMLElement>(null);
    useEffect(() => {
        const closeOutside = (event: PointerEvent) => {
            if (details.current?.open && !details.current.contains(event.target as Node)) details.current.open = false;
        };
        document.addEventListener('pointerdown', closeOutside);
        return () => document.removeEventListener('pointerdown', closeOutside);
    }, []);
    const navigate = (direction: number) => {
        if (details.current) details.current.open = false;
        onNavigate(direction);
    };

    return (
        <section className="summary-review-toolbar" aria-label={t('summaryEditor.changes.title')}>
            <div className="summary-review-explanation">
                <span className={`summary-review-status${active ? ' is-active' : ''}${blocked ? ' is-blocked' : ''}`} role="status" aria-live="polite">
                    <span className="summary-review-status-dot" aria-hidden="true" />
                    <strong title={status}>{status}</strong>
                </span>
                <details ref={details} className="summary-review-help"
                    onBlur={event => {
                        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) event.currentTarget.open = false;
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Escape' && details.current?.open) {
                            event.preventDefault();
                            event.stopPropagation();
                            details.current.open = false;
                            helpTrigger.current?.focus();
                        }
                    }}>
                    <summary ref={helpTrigger} aria-label={t('summaryEditor.changes.help')} title={t('summaryEditor.changes.help')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 0 1 5 .5c0 1.5-2.5 2-2.5 3.5M12 16h.01" /></svg>
                    </summary>
                    <div className="summary-review-help-content" role="note">
                        <strong>{t('summaryEditor.changes.title')}</strong>
                        <p>{description}</p>
                    </div>
                </details>
            </div>
            <div className="summary-review-controls">
                <div className="summary-review-navigation" role="group" aria-label={t('summaryEditor.changes.navigation')}>
                    <button type="button" disabled={disabled || !changeCount} onClick={() => navigate(-1)} aria-label={t('summaryEditor.changes.previous')} title={t('summaryEditor.changes.previous')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m14 7-5 5 5 5" /></svg>
                    </button>
                    <span aria-live="polite" aria-label={active ? t('summaryEditor.changes.pending') : undefined}>{active ? '…' : selectedChange >= 0
                        ? t('summaryEditor.changes.position', { current: selectedChange + 1, total: changeCount })
                        : t('summaryEditor.changes.count', { count: changeCount })}</span>
                    <button type="button" disabled={disabled || !changeCount} onClick={() => navigate(1)} aria-label={t('summaryEditor.changes.next')} title={t('summaryEditor.changes.next')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m10 7 5 5-5 5" /></svg>
                    </button>
                </div>
                <div className="summary-review-actions" role="group" aria-label={t('summaryEditor.changes.title')}>
                    <button type="button" disabled={disabled || !canRestore} onClick={onRestore} title={t('summaryEditor.changes.restoreHint')} aria-label={t('summaryEditor.changes.restore')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5-5 5 5 5M4 10h10a5 5 0 0 1 0 10h-3" /></svg>
                    </button>
                    <button type="button" className="summary-review-accept" disabled={disabled} onClick={onKeep} aria-label={t('summaryEditor.changes.keep')} title={t('summaryEditor.changes.keepHint')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default SummaryChangesReview;
